/**
 * ExtractEngine — orchestrates extraction from an ISO/UDF tree to disk.
 *
 * Sink chain:
 *   1. FSA writable (preferred): showDirectoryPicker() → streamed writes.
 *   2. Per-file Blob fallback: each file becomes a download, capped 500 MB.
 *   3. ZIP-all fallback: STORE-only ZIP via __FileTransfer.FolderZip.wrap().
 *
 * Selection state, cancel, progress accounting all in this engine.
 *
 * Surface: window.__DiskImage.ExtractEngine
 *   var e = new ExtractEngine({ tree, fs: 'iso9660'|'udf', mountedFs, onProgress, onLog });
 *   e.toggleNode(node, recursive=false)
 *   e.selectAll() / e.selectNone()
 *   e.selection() → Set<Node>
 *   await e.run({ mode: 'fsa'|'per-file'|'zip', dirHandle? }) → { ok, total, written, errors[] }
 *   e.cancel()
 *
 * onProgress({ phase, current, total, file, bytesWritten })
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.ExtractEngine) return;

    var FsaSink = w.__FsaSink;
    var Folder = w.__FileTransfer && w.__FileTransfer.FolderZip;

    function ExtractEngine(opts) {
        if (!opts || !opts.tree) throw new Error('ExtractEngine: tree required');
        this.tree = opts.tree;
        this.mountedFs = opts.mountedFs || null; // e.g. an Iso9660 or Udf instance, used for streamFile
        this.fs = opts.fs || 'iso9660';
        this.onProgress = opts.onProgress || function () {};
        this.onLog = opts.onLog || function () {};
        this._selected = new Set();
        this._cancelled = false;
    }

    function _displayName(node) {
        return node.posixName || node.unicodeName || node.name || '(unnamed)';
    }
    function _relPath(node, base) {
        // node.path = '/a/b/c' — strip leading '/' and use display names if available
        var parts = [];
        var cur = node;
        while (cur && cur._parent) {
            parts.unshift(_displayName(cur));
            cur = cur._parent;
        }
        return parts.join('/');
    }

    ExtractEngine.prototype.toggleNode = function (node, recursive) {
        if (!node) return;
        if (this._selected.has(node)) {
            this._removeRecursive(node);
        } else {
            this._addRecursive(node, recursive);
        }
    };
    ExtractEngine.prototype._addRecursive = function (node, recursive) {
        if (!node) return;
        this._selected.add(node);
        if ((recursive !== false) && node.isDir && node.children) {
            node.children.forEach(function (c) { this._addRecursive(c, true); }, this);
        }
    };
    ExtractEngine.prototype._removeRecursive = function (node) {
        if (!node) return;
        this._selected.delete(node);
        if (node.isDir && node.children) {
            node.children.forEach(function (c) { this._removeRecursive(c); }, this);
        }
    };
    ExtractEngine.prototype.selectAll = function () {
        var self = this;
        function walk(n) {
            if (!n) return;
            self._selected.add(n);
            if (n.children) n.children.forEach(walk);
        }
        walk(this.tree);
    };
    ExtractEngine.prototype.selectNone = function () { this._selected.clear(); };
    ExtractEngine.prototype.selection = function () { return new Set(this._selected); };
    ExtractEngine.prototype.cancel = function () { this._cancelled = true; };

    ExtractEngine.prototype._collectFiles = function () {
        // Files only (skip dirs and symlinks for v1); preserve tree order
        var files = [];
        var seen = new Set();
        var self = this;
        function walk(n) {
            if (!n) return;
            if (n.isDir) {
                if (n.children) n.children.forEach(walk);
                return;
            }
            if (n.isSymlink) return;
            if (!self._selected.has(n)) return;
            if (seen.has(n)) return;
            seen.add(n);
            files.push(n);
        }
        if (this._selected.size === 0) {
            // No explicit selection → all files
            walk(this.tree);
        } else {
            walk(this.tree);
        }
        return files;
    };

    ExtractEngine.prototype._streamNode = function (node) {
        // Returns ReadableStream<Uint8Array>
        if (this.mountedFs && typeof this.mountedFs.streamFile === 'function') {
            return this.mountedFs.streamFile(node);
        }
        // No mountedFs: best-effort via direct LBA read (ISO9660 only)
        var SECTOR = 2048;
        var off = node.lba * SECTOR;
        var len = node.size;
        if (this.tree && this.tree._parent === null && this.fs === 'iso9660' && len > 0) {
            var reader = this.mountedFs && this.mountedFs.reader;
            if (reader) return reader.streamRange(off, len);
        }
        throw new Error('ExtractEngine: cannot stream node — no mountedFs');
    };

    ExtractEngine.prototype.run = function (opts) {
        opts = opts || {};
        var mode = opts.mode || 'per-file';
        var files = this._collectFiles();
        var totalBytes = files.reduce(function (s, f) { return s + (f.size || 0); }, 0);
        this.onLog({ event: 'start', mode: mode, fileCount: files.length, totalBytes: totalBytes });

        if (mode === 'fsa') return this._runFsa(files, opts.dirHandle);
        if (mode === 'zip') return this._runZip(files, opts.zipName || 'extracted.zip');
        return this._runPerFile(files);
    };

    ExtractEngine.prototype._runFsa = function (files, dirHandle) {
        if (!FsaSink || !dirHandle) {
            return Promise.reject(new Error('FSA sink unavailable; pass dirHandle and load fsa-writable-fallback.js'));
        }
        var sink = FsaSink.create(dirHandle);
        var self = this;
        var totalBytes = files.reduce(function (s, f) { return s + (f.size || 0); }, 0);
        var written = 0;
        var errors = [];
        var p = Promise.resolve();
        files.forEach(function (n, i) {
            p = p.then(function () {
                if (self._cancelled) throw new DOMException('cancelled', 'AbortError');
                self.onProgress({ phase: 'extracting', current: i, total: files.length, file: n, bytesWritten: written });
                var rel = _relPath(n);
                return sink.openFile(rel).then(function (writer) {
                    var stream = self._streamNode(n);
                    var reader = stream.getReader();
                    function pump() {
                        return reader.read().then(function (r) {
                            if (r.done) return;
                            return writer.write(r.value).then(function () {
                                written += r.value.byteLength;
                                self.onProgress({ phase: 'extracting', current: i, total: files.length, file: n, bytesWritten: written });
                                return pump();
                            });
                        });
                    }
                    return pump().then(function () { return writer.close(); });
                }).catch(function (err) {
                    errors.push({ file: _relPath(n), error: String(err && err.message || err) });
                });
            });
        });
        return p.then(function () {
            return { ok: errors.length === 0, total: files.length, written: written, errors: errors, mode: 'fsa' };
        });
    };

    ExtractEngine.prototype._runPerFile = function (files) {
        var self = this;
        var sink = FsaSink ? FsaSink.create(null) : null;
        var written = 0;
        var errors = [];
        // Sink module missing: hold the results and hand them over as ONE
        // archive at the end. The loop of downloads this used to do delivers
        // exactly one file — a browser grants one download per user gesture and
        // drops the rest in silence, then stops delivering downloads in that tab
        // for the whole origin (measured, real Chrome 152).
        var loose = [];
        var p = Promise.resolve();
        files.forEach(function (n, i) {
            p = p.then(function () {
                if (self._cancelled) throw new DOMException('cancelled', 'AbortError');
                self.onProgress({ phase: 'extracting', current: i, total: files.length, file: n, bytesWritten: written });
                var rel = _relPath(n);
                if (!sink) {
                    return self._streamToBlob(n).then(function (blob) {
                        loose.push({ name: _displayName(n), blob: blob });
                        written += blob.size;
                    }).catch(function (err) {
                        errors.push({ file: rel, error: String(err && err.message || err) });
                    });
                }
                return sink.openFile(rel).then(function (writer) {
                    var stream = self._streamNode(n);
                    var reader = stream.getReader();
                    function pump() {
                        return reader.read().then(function (r) {
                            if (r.done) return;
                            return writer.write(r.value).then(pump);
                        });
                    }
                    return pump().then(function () { return writer.close(); }).then(function (rec) {
                        written += rec.size;
                    });
                }).catch(function (err) {
                    errors.push({ file: rel, error: String(err && err.message || err) });
                });
            });
        });
        return p.then(function () {
            var done = function () {
                return { ok: errors.length === 0, total: files.length, written: written, errors: errors, mode: 'per-file' };
            };
            if (sink) return Promise.resolve(sink.flushFallback()).then(done);
            if (!loose.length) return done();
            var W = typeof window !== 'undefined' ? window : null;
            var have = (W && W.__batchDeliver) ? Promise.resolve(W.__batchDeliver)
                : ((W && W.__ensureBatchDeliver) ? W.__ensureBatchDeliver() : Promise.reject(new Error('no-loader')));
            return have.then(function (BD) {
                return BD.deliver(loose, { skipFolder: true }).then(done);
            }, function () {
                _downloadBlob(loose[0].blob, loose[0].name);
                if (W && W.__dlHold) loose.slice(1).forEach(function (f) { W.__dlHold.add(f.blob, f.name); });
                return done();
            });
        });
    };

    ExtractEngine.prototype._streamToBlob = function (node) {
        var stream = this._streamNode(node);
        var chunks = [];
        var reader = stream.getReader();
        function pump() {
            return reader.read().then(function (r) {
                if (r.done) return;
                chunks.push(r.value);
                return pump();
            });
        }
        return pump().then(function () {
            return new Blob(chunks, { type: 'application/octet-stream' });
        });
    };

    ExtractEngine.prototype._runZip = function (files, zipName) {
        var self = this;
        // Build a list of File-shaped objects with .size, .name, .slice() that streams via reader
        if (!Folder || typeof Folder.wrap !== 'function') {
            // Folder helper unavailable — fall back to building Blobs first then zipping
            return self._runZipFallback(files, zipName);
        }
        var fileLikes = [];
        files.forEach(function (n) {
            fileLikes.push(_makeStreamingBlob(self, n));
        });
        return Promise.resolve(Folder.wrap(fileLikes, { name: zipName })).then(function (zipBlobLike) {
            var blob = (zipBlobLike instanceof Blob) ? zipBlobLike : zipBlobLike.slice(0, zipBlobLike.size);
            _downloadBlob(blob, zipName);
            return { ok: true, total: files.length, written: blob.size, errors: [], mode: 'zip' };
        });
    };

    ExtractEngine.prototype._runZipFallback = function (files, zipName) {
        var self = this;
        // Pre-materialise each file as a Blob, then assemble a ZIP using folder-zip directly with real Files
        var workItems = [];
        var p = Promise.resolve();
        files.forEach(function (n) {
            p = p.then(function () {
                if (self._cancelled) throw new DOMException('cancelled', 'AbortError');
                return self._streamToBlob(n).then(function (b) {
                    var name = _relPath(n);
                    workItems.push(_blobAsFile(b, name));
                });
            });
        });
        return p.then(function () {
            if (Folder && Folder.wrap) {
                var zip = Folder.wrap(workItems, { name: zipName });
                var sliced = (zip.slice && typeof zip.slice === 'function') ? zip.slice(0, zip.size) : zip;
                var blob = (sliced instanceof Blob) ? sliced : new Blob([sliced], { type: 'application/zip' });
                _downloadBlob(blob, zipName);
                return { ok: true, total: files.length, written: zip.size, errors: [], mode: 'zip' };
            }
            // No FolderZip — just download a single concatenated blob? No — refuse.
            return Promise.reject(new Error('ZIP encoder not available'));
        });
    };

    function _makeStreamingBlob(engine, node) {
        var name = _relPath(node);
        var size = node.size || 0;
        // FolderZip's wrap accepts File-shape; .slice() must work.
        var streamRangeReady = engine.mountedFs && engine.mountedFs.reader && typeof engine.mountedFs.reader.streamRange === 'function';
        if (!streamRangeReady) {
            // Fallback: read whole file into memory once (used by _runZipFallback)
            return null;
        }
        // Build a synthetic File-like that satisfies FolderZip's needs
        var SECTOR = 2048;
        var off = (node.lba || 0) * SECTOR;
        var fakeFile = {
            name: name,
            size: size,
            type: 'application/octet-stream',
            lastModified: Date.now(),
            slice: function (start, end) {
                start = start | 0;
                end = (end == null) ? size : (end | 0);
                if (start < 0) start = 0;
                if (end > size) end = size;
                if (end <= start) return new Blob([], { type: 'application/octet-stream' });
                // Read from underlying reader; bytes() returns Uint8Array — wrap in Blob
                return engine.mountedFs.reader.file.slice(off + start, off + end);
            }
        };
        return fakeFile;
    }

    function _blobAsFile(blob, name) {
        return {
            name: name,
            size: blob.size,
            type: blob.type || 'application/octet-stream',
            lastModified: Date.now(),
            slice: function (s, e) { return blob.slice(s, e); }
        };
    }

    function _downloadBlob(blob, name) {
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
            try { document.body.removeChild(a); } catch (_) {}
            URL.revokeObjectURL(url);
        }, 4000);
    }

    w.__DiskImage.ExtractEngine = ExtractEngine;
})(window);
